import { Text, Flex, Checkbox } from "@chakra-ui/react";
import NewTable from "components/NewTable";
import { useState } from "react";
import { UseFormWatch, Control, UseFormSetValue } from "react-hook-form";
import { FormAcordo, FormAcordoParcela } from "features/acordo/typings";
import moment from "moment";
import { useCallback, useEffect, useMemo } from "react";
import Field from "components/Input/Field";
import { Row, Col } from "antd";
import GraficoAcordo from "./GraficoAcordo";
import useListagemParcelasAcordoColumns from "./useListagemParcelasAcordoColumns";
import InputMoney from "components/Input/InputMoney";
import { roundTo } from "round-to";

interface TabelaParcelaProps {
  control: Control<FormAcordo>;
  watch: UseFormWatch<FormAcordo>;
  setValue: UseFormSetValue<FormAcordo>;
  total: number;
  onDistributionChange?: (isDistributedCorrectly: boolean) => any;
}

function TabelaParcela({
  watch,
  total,
  control,
  setValue,
  onDistributionChange,
}: TabelaParcelaProps) {
  const parcelas = watch("parcelas");
  const quantidadeParcela = watch("quantidadeParcela");

  const [splitEqually, setSplitEqually] = useState(false);

  const dataPrimeiraParcela = watch("dataPrimeiraParcela");

  const splitValueEqually = useCallback(
    (value: number) => {
      const copy = [...parcelas];

      const sumToAdd = roundTo(value / quantidadeParcela, 6);

      let sum = 0;
      copy.forEach((parcela) => {
        const valor = roundTo(parcela.valor + sumToAdd, 2);

        sum += valor;
        parcela.valor = roundTo(parcela.valor + sumToAdd, 2);
      });

      if (sum < total) {
        copy[0].valor += roundTo(total - sum, 6);
      } else if (sum > total) {
        copy[copy.length - 1].valor -= roundTo(sum - total, 6);
      }

      setValue("parcelas", copy);
    },
    [quantidadeParcela, parcelas, setValue, total]
  );

  const splitAllEqually = useCallback(
    (quantidadeParcela: number) => {
      const items: FormAcordoParcela[] = [];
      let sum = 0;
      const valorParcela = roundTo(total / quantidadeParcela, 2);

      for (let i = 0; i < quantidadeParcela; i++) {
        const data = dataPrimeiraParcela;
        const parcela = i + 1;

        items.push({
          dataVencimento: moment(data).add(i, "month").format("YYYY-MM-DD"),
          numeroParcela: parcela,
          valor: valorParcela,
        });

        sum += valorParcela;
      }

      const extraAmmount = sum - total;
      const shouldReduceLast = extraAmmount > 1e-3;
      const shouldAddToFirst = quantidadeParcela * valorParcela < total;

      if (shouldReduceLast) {
        items[items.length - 1].valor = roundTo(
          items[items.length - 1].valor - extraAmmount,
          2
        );
      } else if (shouldAddToFirst) {
        items[0].valor = roundTo(items[0].valor + -extraAmmount, 2);
      }

      setValue("parcelas", items);
    },
    [dataPrimeiraParcela, setValue, total]
  );

  const entradaValor = watch("entrada.valor");

  useEffect(() => {
    if (!parcelas[0]?.valor || parcelas.length !== quantidadeParcela) {
      splitAllEqually(quantidadeParcela);
    }
  }, [splitAllEqually, parcelas, quantidadeParcela]);

  useEffect(() => {
    if (splitEqually) {
      splitAllEqually(quantidadeParcela);
    }
  }, [splitAllEqually, splitEqually, entradaValor, quantidadeParcela]);

  const enabledEdit = !splitEqually;

  const columns = useListagemParcelasAcordoColumns({ control, enabledEdit });

  const somaParcelas = roundTo(
    parcelas.reduce((acc, next) => (acc += next.valor), 0),
    2
  );

  const remainingValue = useMemo(
    () => roundTo(total - somaParcelas, 2) as number,
    [somaParcelas, total]
  );

  const hasRemainingValue = remainingValue > 1e-3;

  useEffect(() => {
    if (onDistributionChange) {
      onDistributionChange?.(Math.abs(remainingValue) < 1e-3);
    }
  }, [onDistributionChange, remainingValue]);

  return (
    <>
      <Row gutter={16} style={{ width: "100%", marginBottom: "20px" }}>
        <Col sm={4}>
          <Field
            label="Quantidade de parcelas"
            control={control}
            name="quantidadeParcela"
            render={({ field }) => (
              <InputMoney
                value={field.value}
                onChange={field.onChange}
                placeholder="0"
                maxValue={360}
                decimalScale={0}
                decimalsLimit={0}
                step={1}
              />
            )}
          />
          <Flex></Flex>
        </Col>
        <Col sm={4}>
          <Field
            label="Primeira parcela em"
            control={control}
            name="dataPrimeiraParcela"
            inputProps={{
              type: "date",
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={16}>
          <Flex mt={"32px"} mb="16px">
            <Text fontSize="18px" fontWeight="medium">
              Parcelas
            </Text>
          </Flex>
          <Checkbox
            mb="16px"
            isChecked={splitEqually}
            onChange={() => {
              splitAllEqually(quantidadeParcela);
              setSplitEqually((state) => !state);
            }}
          >
            <Text fontSize="15px" color="gray.700">
              Distribuir o valor total igualmente entre as parcelas
            </Text>
          </Checkbox>
          <NewTable<FormAcordoParcela>
            loading={false}
            size="small"
            columns={[...columns]}
            dataSource={parcelas}
          />
        </Col>
        <Col sm={8}>
          <Flex ml={16} mt={"32px"} mb="16px">
            <Text fontSize="18px">Soma das parcelas</Text>
          </Flex>
          <GraficoAcordo
            restamParcelas={roundTo(remainingValue, 3)}
            somaParcelas={somaParcelas}
          />
          {hasRemainingValue ? (
            <Flex justify="center" mt={4}>
              <Checkbox
                isChecked={false}
                onChange={() => splitValueEqually(remainingValue)}
              >
                <Text fontSize="15px" color="gray.700">
                  Distribuir restante igualmente
                </Text>
              </Checkbox>
            </Flex>
          ) : null}
        </Col>
      </Row>
    </>
  );
}

export default TabelaParcela;
